/**
 * @fileoverview Baselinker integration setup wizard page.
 * @module features/baselinker-setup/pages/BaselinkerSetupPage
 */

import { ReactElement, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft,
  CheckCircle2,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Package,
  List,
  Search,
} from 'lucide-react'
import { z } from 'zod'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Kbd } from '@/components/ui/kbd'
import { ScrollReveal } from '@/shared/components/ui'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { useIntegrationsStatus } from '@/features/settings/hooks/useIntegrationsStatus'
import { getBaselinkerCurrency, setBaselinkerCurrency } from '@/features/settings/api/baselinkerCurrencyService'
import { getOrgSettings } from '@/features/settings/api/orgSettingsService'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import apiClient from '@/lib/apiClient'
import {
  listStatusMappings,
  updateStatusMapping,
  type ViewType,
} from '@/features/status-mappings/api/statusMappingsService'

/**
 * Enables scheduled sync for an integration after setup wizard completes.
 */
async function enableIntegrationSync(orgId: string, provider: string): Promise<void> {
  await apiClient.post(`/orgs/${orgId}/integrations/${provider}/enable-sync`)
}
import {
  useBaselinkerInventoriesQuery,
  useImportPreviewQuery,
  useImportProductsMutation,
} from '@/features/baselinker-import/api/baselinkerImportService'
import { BaselinkerProductsTable } from '@/features/baselinker-import/components/BaselinkerProductsTable'

type WizardStep = 0 | 1 | 2 | 3 | 4 | 5

const BaseLinkerCredentialsSchema = z.object({
  blApiToken: z.string().min(1, 'API token is required'),
})

export default function BaselinkerSetupPage(): ReactElement {
  const { t } = useTranslation('features-integrations')
  const navigate = useNavigate()
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<WizardStep>(0)
  const [token, setToken] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ productsImported: number } | null>(null)

  // Queries
  const integrationsQuery = useIntegrationsStatus()
  const baselinkerStatus = integrationsQuery.data?.find(i => i.provider === 'BASELINKER')
  const isConnected = baselinkerStatus?.status === 'ACTIVE'

  const orgSettingsQuery = useQuery({
    queryKey: activeOrgId ? ['org', activeOrgId, 'settings'] : ['settings', 'no-org'],
    queryFn: () => getOrgSettings(OrgIdSchema.parse(activeOrgId)),
    enabled: !!activeOrgId,
    staleTime: 5 * 60 * 1000,
  })

  const currencyQuery = useQuery({
    queryKey: ['baselinker', 'currency'],
    queryFn: getBaselinkerCurrency,
    enabled: isConnected,
    staleTime: 5 * 60 * 1000,
  })

  // Esc to go back
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        navigate('/dashboard/settings')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  const goToStep = (nextStep: WizardStep): void => {
    setStep(nextStep)
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 lg:px-8 pt-24 pb-8 space-y-8">
        <ScrollReveal blur>
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/settings')}
              className="gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              {t('setup.title', 'Connect sales from BaseLinker')}
            </h1>
          </div>
        </ScrollReveal>

        <div className="flex flex-col gap-4 pb-8">
          {/* Step 0: Find Token Guide */}
          <ScrollReveal delay={0.1}>
            <Step0Guide
              isActive={step === 0}
              isCompleted={step > 0}
              onContinue={() => goToStep(1)}
              onReopen={() => goToStep(0)}
            />
          </ScrollReveal>

          {/* Step 1: Connect */}
          {step >= 1 && (
            <ScrollReveal delay={0.15}>
              <Step1Connect
                isActive={step === 1}
                isCompleted={step > 1}
                isLocked={step < 1}
                token={token}
                showToken={showToken}
                onTokenChange={setToken}
                onToggleShowToken={() => setShowToken(prev => !prev)}
                onContinue={() => goToStep(2)}
                queryClient={queryClient}
              />
            </ScrollReveal>
          )}

          {/* Step 2: Currency */}
          {step >= 2 && (
            <ScrollReveal delay={0.2}>
              <Step2Currency
                isActive={step === 2}
                isCompleted={step > 2}
                isLocked={step < 2}
                selectedCurrency={selectedCurrency ?? currencyQuery.data?.currency ?? null}
                allowedCurrencies={orgSettingsQuery.data?.allowedReportingCurrencies ?? []}
                onCurrencyChange={setSelectedCurrency}
                onContinue={() => goToStep(3)}
                queryClient={queryClient}
              />
            </ScrollReveal>
          )}

          {/* Step 3: Import Products */}
          {step >= 3 && (
            <ScrollReveal delay={0.25}>
              <Step3ImportProducts
                isActive={step === 3}
                isCompleted={step > 3}
                isLocked={step < 3}
                onContinue={(result) => {
                  setImportResult(result)
                  goToStep(4)
                }}
                onSkip={() => {
                  setImportResult({ productsImported: 0 })
                  goToStep(4)
                }}
              />
            </ScrollReveal>
          )}

          {/* Step 4: Status Mappings */}
          {step >= 4 && (
            <ScrollReveal delay={0.3}>
              <Step4Mappings
                isActive={step === 4}
                isCompleted={step > 4}
                isLocked={step < 4}
                onContinue={async () => {
                  // Enable scheduled sync and trigger initial historical sync
                  if (activeOrgId) {
                    try {
                      await enableIntegrationSync(activeOrgId, 'BASELINKER')
                      toast.success(t('setup.steps.mappings.syncStarted', 'Syncing your order data...'))
                    } catch (error) {
                      console.error('Failed to enable sync:', error)
                      // Continue anyway - sync can be enabled manually later
                    }
                  }
                  goToStep(5)
                }}
              />
            </ScrollReveal>
          )}

          {/* Step 5: Done */}
          {step >= 5 && (
            <ScrollReveal delay={0.35}>
              <Step5Done
                isActive={step === 5}
                isLocked={step < 5}
                importResult={importResult}
                onClose={() => navigate('/dashboard/settings')}
              />
            </ScrollReveal>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Step Components ---

interface StepProps {
  isActive: boolean
  isCompleted: boolean
  isLocked: boolean
}

function Step0Guide({
  isActive,
  isCompleted,
  onContinue,
  onReopen,
}: Omit<StepProps, 'isLocked'> & { onContinue: () => void; onReopen: () => void }): ReactElement {
  const { t } = useTranslation('features-integrations')

  return (
    <section
      className={cn(
        'flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 transition-all duration-300',
        isActive && 'ring-2 ring-primary/20',
        isCompleted && 'opacity-80 cursor-pointer hover:opacity-100'
      )}
      onClick={isCompleted ? onReopen : undefined}
    >
      <div className={cn(!isCompleted && 'mb-4')}>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
              isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'
            )}
          >
            {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '0'}
          </div>
          {t('setup.steps.guide.title', 'Find your API Token')}
        </h3>
      </div>

      {!isCompleted && (
        <div className="ml-10 space-y-6">
          {/* Guide steps - 3 columns */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-3">
              <div className="rounded-lg border border-border/30 overflow-hidden">
                <img src="/base-integration-guide/bs-step1.webp" alt="Step 1" className="w-full" />
              </div>
              <div className="flex items-start gap-2">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                  1
                </div>
                <span className="text-sm text-muted-foreground text-left">
                  {t('setup.steps.guide.step1', 'Click profile → "My Account"')}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="rounded-lg border border-border/30 overflow-hidden">
                <img src="/base-integration-guide/bs-step2.webp" alt="Step 2" className="w-full" />
              </div>
              <div className="flex items-start gap-2">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                  2
                </div>
                <span className="text-sm text-muted-foreground text-left">
                  {t('setup.steps.guide.step2', 'Go to the "API" tab')}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="rounded-lg border border-border/30 overflow-hidden">
                <img src="/base-integration-guide/bs-step3.webp" alt="Step 3" className="w-full" />
              </div>
              <div className="flex items-start gap-2">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                  3
                </div>
                <span className="text-sm text-muted-foreground text-left">
                  {t('setup.steps.guide.step3', 'Copy your API token')}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border/40">
            <a
              href="https://panel.baselinker.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <Button variant="outline" size="sm" className="gap-1.5 text-muted-foreground">
                <img src="/base-favicon.png" alt="" className="size-4 rounded-sm" />
                {t('setup.steps.guide.openBaselinker', 'Open Baselinker')}
              </Button>
            </a>
            <Button variant="outline" size="sm" onClick={onContinue} className="gap-1.5">
              {t('setup.steps.guide.continue', 'I have my token')}
              <ArrowRight className="size-3.5" />
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

function Step1Connect({
  isActive,
  isCompleted,
  isLocked,
  token,
  showToken,
  onTokenChange,
  onToggleShowToken,
  onContinue,
  queryClient,
}: StepProps & {
  token: string
  showToken: boolean
  onTokenChange: (token: string) => void
  onToggleShowToken: () => void
  onContinue: () => void
  queryClient: ReturnType<typeof useQueryClient>
}): ReactElement {
  const { t } = useTranslation('features-integrations')
  const { activeOrgId } = useActiveOrg()

  const connectMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!activeOrgId) throw new Error('No active organization')
      const orgId = OrgIdSchema.parse(activeOrgId) as unknown as string
      const payload = BaseLinkerCredentialsSchema.parse({ blApiToken: token })
      await apiClient.put(`/orgs/${orgId}/integrations/BASELINKER/credentials`, payload)
    },
    onSuccess: async () => {
      toast.success(t('setup.steps.connect.success', 'Connected to Baselinker'))
      // Sync is deferred until status mappings are configured
      await queryClient.invalidateQueries({ queryKey: ['integrations', 'status'] })
      onContinue()
    },
    onError: (e: unknown) => {
      const axiosError = e as { response?: { data?: { message?: string } } }
      const serverMessage = axiosError.response?.data?.message

      if (serverMessage?.includes('Invalid') && serverMessage?.includes('token')) {
        toast.error(t('setup.steps.connect.invalidToken', 'Invalid API token. Please check and try again.'))
      } else {
        toast.error(t('setup.steps.connect.error', 'Failed to connect'))
      }
    },
  })

  const handleConnect = (): void => {
    if (token.trim().length === 0) return
    connectMutation.mutate()
  }

  return (
    <section
      className={cn(
        'flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 transition-all duration-300',
        isActive && 'ring-2 ring-primary/20',
        isCompleted && 'opacity-80',
        isLocked && 'opacity-50'
      )}
    >
      <div className={cn(!isCompleted && isActive && 'mb-4')}>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
              isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'
            )}
          >
            {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '1'}
          </div>
          {t('setup.steps.connect.title', 'Connect')}
        </h3>
        <p className="text-sm text-muted-foreground ml-10">
          {t('setup.steps.connect.subtitle', 'Enter your API token to connect')}
        </p>
      </div>

      {isActive && !isCompleted && (
        <div className="ml-10 space-y-4">
          <div className="max-w-md">
            <Label htmlFor="api-token" className="text-sm font-medium mb-2 block">
              {t('setup.steps.connect.label', 'API Token')}
            </Label>
            <div className="relative">
              <Input
                id="api-token"
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => onTokenChange(e.target.value)}
                placeholder={t('setup.steps.connect.placeholder', 'Enter your Baselinker API token')}
                className="pr-10"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
              />
              <button
                type="button"
                onClick={onToggleShowToken}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('setup.steps.connect.hint', 'Your token is stored securely and never shared')}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleConnect}
              disabled={token.trim().length === 0 || connectMutation.isPending}
              className="gap-1.5"
            >
              {connectMutation.isPending ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  {t('setup.steps.connect.connecting', 'Connecting...')}
                </>
              ) : (
                <>
                  {t('setup.steps.connect.button', 'Connect')}
                  <ArrowRight className="size-3.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

function Step2Currency({
  isActive,
  isCompleted,
  isLocked,
  selectedCurrency,
  allowedCurrencies,
  onCurrencyChange,
  onContinue,
  queryClient,
}: StepProps & {
  selectedCurrency: string | null
  allowedCurrencies: string[]
  onCurrencyChange: (currency: string) => void
  onContinue: () => void
  queryClient: ReturnType<typeof useQueryClient>
}): ReactElement {
  const { t } = useTranslation('features-integrations')

  const currencyMutation = useMutation({
    mutationFn: setBaselinkerCurrency,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['baselinker', 'currency'] })
      onContinue()
    },
    onError: () => {
      toast.error(t('setup.steps.currency.error', 'Failed to save currency'))
    },
  })

  const handleContinue = (): void => {
    if (!selectedCurrency) return
    currencyMutation.mutate(selectedCurrency)
  }

  return (
    <section
      className={cn(
        'flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 transition-all duration-300',
        isActive && 'ring-2 ring-primary/20',
        isCompleted && 'opacity-80',
        isLocked && 'opacity-50'
      )}
    >
      <div className={cn(!isCompleted && isActive && 'mb-4')}>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
              isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'
            )}
          >
            {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '2'}
          </div>
          {t('setup.steps.currency.title', 'Currency')}
        </h3>
        <p className="text-sm text-muted-foreground ml-10">
          {t('setup.steps.currency.subtitle', 'for cost of goods')}
        </p>
      </div>

      {isActive && !isCompleted && (
        <div className="ml-10 space-y-4">
          <div className="max-w-xs">
            <Select value={selectedCurrency ?? ''} onValueChange={onCurrencyChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('setup.steps.currency.placeholder', 'Select currency')} />
              </SelectTrigger>
              <SelectContent>
                {allowedCurrencies.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleContinue}
            disabled={!selectedCurrency || currencyMutation.isPending}
            className="gap-1.5"
          >
            {currencyMutation.isPending ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                {t('setup.steps.currency.saving', 'Saving...')}
              </>
            ) : (
              <>
                {t('setup.steps.currency.continue', 'Continue')}
                <ArrowRight className="size-3.5" />
              </>
            )}
          </Button>
        </div>
      )}
    </section>
  )
}

function Step3ImportProducts({
  isActive,
  isCompleted,
  isLocked,
  onContinue,
  onSkip,
}: StepProps & {
  onContinue: (result: { productsImported: number }) => void
  onSkip: () => void
}): ReactElement {
  const { t } = useTranslation('features-integrations')
  const [selectedInventoryId, setSelectedInventoryId] = useState<number | null>(null)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set())
  const [selectedVariantSkus, setSelectedVariantSkus] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  const inventoriesQuery = useBaselinkerInventoriesQuery(isActive)
  const importMutation = useImportProductsMutation()

  // Derive effective inventory ID: auto-select first inventory when data loads
  // This avoids cascading renders from setState in useEffect
  const effectiveInventoryId = selectedInventoryId ?? (isActive ? inventoriesQuery.data?.[0]?.inventoryId : null) ?? null

  const previewQuery = useImportPreviewQuery(effectiveInventoryId, isActive && effectiveInventoryId !== null)

  const allProducts = previewQuery.data?.newProducts ?? []

  const handleSelectAll = (): void => {
    const newProductIds = new Set<string>()
    const newVariantSkus = new Set<string>()

    allProducts.forEach(p => {
      const importableVariants = p.variants?.filter(v => !v.alreadyExists) ?? []
      const hasVariants = (p.variants?.length ?? 0) > 0

      if (!hasVariants && !p.skuAlreadyExists) {
        newProductIds.add(p.baselinkerProductId)
      } else if (importableVariants.length === p.variants?.length) {
        newProductIds.add(p.baselinkerProductId)
      } else if (importableVariants.length > 0) {
        importableVariants.forEach(v => newVariantSkus.add(v.sku))
      }
    })

    setSelectedProductIds(newProductIds)
    setSelectedVariantSkus(newVariantSkus)
  }

  const handleClearAll = (): void => {
    setSelectedProductIds(new Set())
    setSelectedVariantSkus(new Set())
  }

  const handleImport = async (): Promise<void> => {
    if (!effectiveInventoryId || (selectedProductIds.size === 0 && selectedVariantSkus.size === 0)) return

    try {
      const result = await importMutation.mutateAsync({
        inventoryId: effectiveInventoryId,
        productIds: selectedProductIds.size > 0 ? Array.from(selectedProductIds) : undefined,
        variantSkus: selectedVariantSkus.size > 0 ? Array.from(selectedVariantSkus) : undefined,
      })
      onContinue({ productsImported: result.newlyImported })
    } catch {
      // Error handled by mutation
    }
  }

  const totalSelected = selectedProductIds.size + selectedVariantSkus.size

  // Collapsed state (completed or locked)
  if (isCompleted || isLocked) {
    return (
      <section
        className={cn(
          'flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 transition-all duration-300',
          isCompleted && 'opacity-80',
          isLocked && 'opacity-50'
        )}
      >
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
                isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'
              )}
            >
              {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '3'}
            </div>
            {t('setup.steps.import.title', 'Import Products')}
          </h3>
          <p className="text-sm text-muted-foreground ml-10">
            {t('setup.steps.import.subtitle', 'Import products from Baselinker catalog')}
          </p>
        </div>
      </section>
    )
  }

  // Active state - full layout matching Step2Preview
  return (
    <section className="flex flex-col rounded-3xl border border-border/15 bg-card pb-6 pt-6 transition-all duration-300 ring-2 ring-primary/20">
      {/* 3-column header grid */}
      <div className="mb-6 grid grid-cols-[auto_auto_1fr] items-center gap-4 px-6">
        {/* Col 1: Title */}
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold bg-secondary text-secondary-foreground">
            3
          </div>
          {t('setup.steps.import.title', 'Import Products')}
        </h3>

        {/* Col 2: Inventory dropdown */}
        <Select
          value={effectiveInventoryId?.toString() ?? ''}
          onValueChange={(val) => {
            setSelectedInventoryId(parseInt(val, 10))
            setSelectedProductIds(new Set())
            setSelectedVariantSkus(new Set())
            setSearchTerm('')
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('setup.steps.import.selectCatalog', 'Select catalog')} />
          </SelectTrigger>
          <SelectContent>
            {inventoriesQuery.data?.map(inv => (
              <SelectItem key={inv.inventoryId} value={inv.inventoryId.toString()}>
                {inv.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Col 3: Search */}
        <div className="relative w-full max-w-md flex items-center">
          <Search className="absolute left-2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('setup.steps.import.searchPlaceholder', 'Search products...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

      </div>

      {/* Table container */}
      <div className="min-h-[460px]">
        <BaselinkerProductsTable
          products={allProducts}
          selectedProductIds={selectedProductIds}
          selectedVariantSkus={selectedVariantSkus}
          onProductSelectionChange={setSelectedProductIds}
          onVariantSelectionChange={setSelectedVariantSkus}
          isActive={isActive}
          isLoading={inventoriesQuery.isLoading || previewQuery.isLoading}
          isError={previewQuery.isError}
          enableVariantSelection
          searchTerm={searchTerm}
          noBorder
          maxHeight="460px"
          {...(previewQuery.data?.totalProducts !== undefined && { totalProducts: previewQuery.data.totalProducts })}
          {...(previewQuery.data?.alreadyImported !== undefined && { alreadyImported: previewQuery.data.alreadyImported })}
          {...(allProducts.length > 0 && { onSelectAll: handleSelectAll })}
          onClearAll={handleClearAll}
        />
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between px-6 pt-4">
        <Button variant="ghost" onClick={onSkip} className="text-muted-foreground">
          {t('setup.steps.import.skip', 'Skip for now')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleImport}
          disabled={totalSelected === 0 || importMutation.isPending}
          className="gap-1.5"
        >
          {importMutation.isPending ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              {t('setup.steps.import.importing', 'Importing...')}
            </>
          ) : (
            <>
              {t('setup.steps.import.importSelected', { count: totalSelected, defaultValue: `Import ${totalSelected} products` })}
              <ArrowRight className="size-3.5" />
            </>
          )}
        </Button>
      </div>
    </section>
  )
}

interface StatusRow {
  id: number
  externalStatus: string
  statusName: string | null
  viewType: ViewType | null
}

function Step4Mappings({
  isActive,
  isCompleted,
  isLocked,
  onContinue,
}: StepProps & { onContinue: () => void }): ReactElement {
  const { t } = useTranslation('features-integrations')
  const queryClient = useQueryClient()

  const mappingsQuery = useQuery({
    queryKey: ['status-mappings'],
    queryFn: () => listStatusMappings(),
    enabled: isActive,
    staleTime: 60_000,
  })

  const updateMutation = useMutation({
    mutationFn: (params: { id: number; viewType: ViewType }) =>
      updateStatusMapping(params.id, { viewType: params.viewType }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['status-mappings'] })
    },
  })

  // All statuses are pre-populated with viewType = null for unconfigured ones
  const allRows: StatusRow[] = (mappingsQuery.data ?? [])
    .map((m) => ({
      id: m.id,
      externalStatus: m.externalStatus,
      statusName: m.statusName ?? null,
      viewType: m.viewType,
    }))
    .sort((a, b) => {
      // Sort alphabetically by display name to keep stable order
      const nameA = a.statusName ?? a.externalStatus
      const nameB = b.statusName ?? b.externalStatus
      return nameA.localeCompare(nameB)
    })

  const handleMappingChange = (row: StatusRow, newViewType: ViewType): void => {
    updateMutation.mutate({ id: row.id, viewType: newViewType })
  }

  const isLoading = mappingsQuery.isLoading

  return (
    <section
      className={cn(
        'flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 transition-all duration-300',
        isActive && 'ring-2 ring-primary/20',
        isCompleted && 'opacity-80',
        isLocked && 'opacity-50'
      )}
    >
      <div className={cn(!isCompleted && isActive && 'mb-4')}>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
              isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'
            )}
          >
            {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '4'}
          </div>
          {t('setup.steps.mappings.title', 'How to treat your statuses?')}
        </h3>
        <div className="text-sm text-muted-foreground ml-10 space-y-1">
          <p>{t('setup.steps.mappings.hint.cancelled', 'Cancelled — treated as cancelled orders and returns')}</p>
          <p>{t('setup.steps.mappings.hint.sales', 'Sales — treated as revenue')}</p>
          <p>{t('setup.steps.mappings.hint.ignore', 'Ignore — not included in statistics at all')}</p>
        </div>
      </div>

      {isActive && !isCompleted && (
        <div className="ml-10 space-y-4">

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : allRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              {t('setup.steps.mappings.noStatuses', 'No order statuses found. Sync some orders first.')}
            </p>
          ) : (
            <div className="rounded-xl">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-4 pb-3 border-b border-border/40 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('setup.steps.mappings.baselinkerStatus', 'Baselinker Status')}
                </span>
                <span />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('setup.steps.mappings.treatAs', 'Treat as')}
                </span>
              </div>

              {allRows.map((row) => (
                <div
                  key={row.externalStatus}
                  className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center py-3 border-b border-border/20 last:border-b-0"
                >
                  <span className="text-sm truncate" title={row.externalStatus}>
                    {row.statusName ?? row.externalStatus}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <Select
                    value={row.viewType ?? ''}
                    onValueChange={(val) => handleMappingChange(row, val as ViewType)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t('setup.steps.mappings.selectStatus', 'Select...')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SALES_ANALYTICS">{t('setup.steps.mappings.options.sales', 'Sales')}</SelectItem>
                      <SelectItem value="CANCELLATION_ANALYTICS">{t('setup.steps.mappings.options.cancelled', 'Cancelled')}</SelectItem>
                      <SelectItem value="AUDIT">{t('setup.steps.mappings.options.ignore', 'Ignore')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          <Button variant="outline" onClick={onContinue}>
            {t('setup.steps.mappings.continue', 'Continue')}
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      )}
    </section>
  )
}

function Step5Done({
  isActive,
  isLocked,
  importResult,
  onClose,
}: Omit<StepProps, 'isCompleted'> & {
  importResult: { productsImported: number } | null
  onClose: () => void
}): ReactElement {
  const { t } = useTranslation('features-integrations')
  const navigate = useNavigate()

  // Enter to close
  useEffect(() => {
    if (!isActive) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isActive, onClose])

  return (
    <section
      className={cn(
        'flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 transition-all duration-300',
        isActive && 'ring-2 ring-positive/20',
        isLocked && 'opacity-50'
      )}
    >
      <div className={cn(isActive && 'mb-4')}>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
              isActive ? 'text-positive' : 'bg-secondary text-secondary-foreground'
            )}
          >
            {isActive ? <CheckCircle2 className="h-5 w-5" /> : '5'}
          </div>
          {t('setup.steps.done.title', 'Done')}
        </h3>
        <p className="text-sm text-muted-foreground ml-10">
          {t('setup.steps.done.subtitle', 'Setup complete')}
        </p>
      </div>

      {isActive && (
        <div className="ml-10 space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-positive/20 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-positive" />
            </div>
          </div>

          <div className="text-center">
            <h4 className="text-xl font-semibold mb-1">
              {t('setup.steps.done.heading', 'Setup Complete!')}
            </h4>
            <p className="text-sm text-muted-foreground">
              {t('setup.steps.done.description', 'Your Baselinker integration is now active')}
            </p>
          </div>

          {importResult && importResult.productsImported > 0 && (
            <div className="flex justify-center">
              <div className="bg-muted/50 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-positive">{importResult.productsImported}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                  {t('setup.steps.done.productsImported', 'Products Imported')}
                </div>
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-medium mb-3">{t('setup.steps.done.whatsNext', "What's next?")}</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors text-left"
              >
                <Package className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium">{t('setup.steps.done.seeStatistics', 'See statistics')}</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/orders')}
                className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors text-left"
              >
                <List className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium">{t('setup.steps.done.viewOrders', 'View Orders')}</span>
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              {t('setup.steps.done.backToSettings', 'Back to Settings')}
              <Kbd className="ml-2">Enter</Kbd>
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
